/**
 * The oldest unanswered challenge invite, as a slim row (specs/home-page AC-27, AC-28, AC-38): inviter,
 * "{name} challenged you · {stake}", inline Accept. Renders nothing when there is no invite (AC-29).
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { HomeInviteTileModel } from '../../../src/home/types/home.types';
import { HomeRegionSkeleton } from '../HomeRegionState/HomeRegionSkeleton';
import { INVITE_TILE_AVATAR_SIZE, getInviteTileStyles } from './InviteTile.styles';

const numberFormat = new Intl.NumberFormat();

export interface InviteTileProps {
  model: HomeInviteTileModel | null;
  isLoading: boolean;
  /** True while this invite's accept request is in flight. */
  isAccepting: boolean;
  onAccept: (challengeId: string, stakeCoins: number) => void;
  onPress: (challengeId: string) => void;
}

export const InviteTile: React.FC<InviteTileProps> = ({ model, isLoading, isAccepting, onAccept, onPress }) => {
  const theme = useTheme();
  const styles = useMemo(() => getInviteTileStyles(theme), [theme]);
  const challengeId = model?.challengeId;
  const stakeCoins = model?.stakeCoins ?? 0;

  const handlePress = useCallback(() => {
    if (challengeId) onPress(challengeId);
  }, [onPress, challengeId]);

  const handleAccept = useCallback(() => {
    if (!challengeId || isAccepting) return;
    onAccept(challengeId, stakeCoins);
  }, [onAccept, challengeId, stakeCoins, isAccepting]);

  if (isLoading) {
    return <HomeRegionSkeleton variant="tile" testID={`${HOME_TEST_IDS.tileSkeleton}-invite`} />;
  }
  if (!model) return null;

  // Two sibling pressables (open the challenge / accept it) rather than a button inside a button:
  // nested interactive elements are invalid on web and confuse screen readers.
  return (
    <View style={styles.tile}>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.openArea,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.openAreaPressed,
          hovered && Platform.OS === 'web' && styles.openAreaHover,
        ]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={`${model.inviter.name} ${HOME_COPY.INVITE_CHALLENGED_YOU}`}
        testID={HOME_TEST_IDS.inviteTile}
      >
        <FriendAvatar
          avatarId={model.inviter.avatarId ? normalizeDisplayAvatarId(model.inviter.avatarId) : null}
          avatarConfig={model.inviter.avatarConfig}
          fallbackUrl={model.inviter.avatarUrl ?? undefined}
          nameForInitials={model.inviter.name}
          size={INVITE_TILE_AVATAR_SIZE}
          style={styles.avatar}
          small
        />
        <Text style={styles.text} numberOfLines={1}>
          <Text style={styles.name}>{model.inviter.name}</Text>
          <Text style={styles.muted}> {HOME_COPY.INVITE_CHALLENGED_YOU} · </Text>
        </Text>
        <View style={styles.stakeRow}>
          <Image source={IDLE_COIN_SOURCE} style={styles.coin} contentFit="contain" alt={WEB_IMAGE_ALT_DECORATIVE} />
          <Text style={styles.stake}>{numberFormat.format(model.stakeCoins)}</Text>
        </View>
      </Pressable>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.acceptButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.acceptButtonPressed,
          hovered && Platform.OS === 'web' && styles.acceptButtonHover,
          isAccepting && styles.acceptButtonBusy,
        ]}
        onPress={handleAccept}
        disabled={isAccepting}
        accessibilityRole="button"
        accessibilityLabel={HOME_COPY.INVITE_ACCEPT}
        accessibilityState={{ busy: isAccepting, disabled: isAccepting }}
        testID={HOME_TEST_IDS.inviteAccept}
      >
        {isAccepting ? (
          <ActivityIndicator size="small" color={theme.onPrimary} />
        ) : (
          <Text style={styles.acceptText}>{HOME_COPY.INVITE_ACCEPT}</Text>
        )}
      </Pressable>
    </View>
  );
};
